import { useCallback, useMemo } from 'react'

import { useWishlistMutations } from './useWishlistMutations'
import { useWishlistQuery } from './useWishlistQuery'
import { getWishlistConfig } from '../../types/config'

import type { WishlistStateType } from '../../types'

/**
 * Composition hook for wishlist functionality
 * Combines query and mutations into a single state object
 */
export function useWishlistStateHook(): WishlistStateType {
  const {
    wishlistId,
    wishlistItems,
    itemsCount: queryItemsCount,
    isLoggedIn,
    refetch,
  } = useWishlistQuery()

  const isInWishlist = useCallback(
    (sku: string): boolean => {
      return wishlistItems.some(item => item.product.sku === sku)
    },
    [wishlistItems]
  )

  const getWishlistItemId = useCallback(
    (sku: string): string | null => {
      const item = wishlistItems.find(item => item.product.sku === sku)
      return item?.id ?? null
    },
    [wishlistItems]
  )

  const {
    processingWishlist,
    errorMessage,
    itemsCountOverride,
    addToWishlist,
    removeFromWishlist,
  } = useWishlistMutations({
    wishlistId,
    refetch,
    getWishlistItemId,
  })

  // Use override count if available (optimistic update), otherwise use query count
  const itemsCount = itemsCountOverride ?? queryItemsCount

  const handleAddToWishlist = useCallback(
    (sku: string) => {
      if (!isLoggedIn) {
        const config = getWishlistConfig()
        window.location.href = config.loginUrl
        return
      }
      addToWishlist(sku)
    },
    [isLoggedIn, addToWishlist]
  )

  const handleRemoveFromWishlist = useCallback(
    (sku: string) => {
      removeFromWishlist(sku)
    },
    [removeFromWishlist]
  )

  const handleToggleWishlist = useCallback(
    (sku: string) => {
      if (isInWishlist(sku)) {
        handleRemoveFromWishlist(sku)
      } else {
        handleAddToWishlist(sku)
      }
    },
    [isInWishlist, handleAddToWishlist, handleRemoveFromWishlist]
  )

  return useMemo<WishlistStateType>(
    () => ({
      handleAddToWishlist,
      handleRemoveFromWishlist,
      handleToggleWishlist,
      isInWishlist,
      processingWishlist,
      errorMessage,
      itemsCount,
      isLoggedIn,
    }),
    [
      handleAddToWishlist,
      handleRemoveFromWishlist,
      handleToggleWishlist,
      isInWishlist,
      processingWishlist,
      errorMessage,
      itemsCount,
      isLoggedIn,
    ]
  )
}
